<template>
  <div>
    <!-- Text-and-Button:start -->
    <div class="flex items-baseline justify-between mt-10 relative">
      <h2 class="intro-y text-lg font-medium -top-1">
        {{ $t(`${viewRoot}.list`) }}
      </h2>
      <button type="button" class="btn btn-primary w-40 md:w-34 h-8" @click="newPurpose">
        <span>{{ $t(`${viewRoot}.new`) }}</span>
      </button>
    </div>
    <!-- Text-and-Button:end -->

    <!-- Main-container:start -->
    <div class="grid grid-cols-12 gap-6 mb-12">
      <!--Header:start-->
      <div class="intro-y col-span-12 flex flex-col sm:flex-row items-center justify-between mt-2 gap-4">
        <div class="w-full sm:w-auto mr-auto">
          <p>{{ $t(`${viewRoot}.description`) }}</p>
        </div>

        <div class="w-full sm:w-auto relative text-gray-700 dark:text-gray-300">
          <input
            type="text"
            class="form-control w-full sm:w-72 box pr-10 placeholder-theme-13"
            :placeholder="$t(`${viewRoot}.search_text`)"
            v-model="searchInput"
            @keyup="filterPurposes"
          />
          <SearchIcon class="w-4 h-4 absolute my-auto inset-y-0 mr-3 right-0" />
        </div>
      </div>
      <!-- Header:end -->

      <!-- Data-List:start -->
      <div class="intro-y col-span-12 overflow-auto">
        <table class="table table-report -mt-2 overflow-hidden">
          <thead>
            <tr>
              <th class="whitespace-nowrap">
                <div class="grid purposes__grid">
                  <div class="title__table">ID</div>
                  <div class="title__table">{{ $t(`${viewRoot}.table.title1`) }}</div>
                </div>
              </th>

              <th class="text-center whitespace-nowrap">
                {{ $t(`${viewRoot}.table.actions`) }}
              </th>
            </tr>
          </thead>
          <tbody>
            <template v-for="purpose in purposes" :key="purpose.id">
              <tr class="intro-x">
                <td>
                  <div class="grid purposes__grid cursor-pointer">
                    <!--ID Purpose-->
                    <div>{{ purpose.id }}</div>

                    <!--Title-->
                    <div>{{ purpose.name }}</div>
                  </div>
                </td>

                <!-- Section-Actions:start -->
                <td>
                  <div class="flex justify-center items-center">
                    <a class="flex items-center mr-3" href="javascript:;" @click="editPurpose(purpose.id)">
                      <CheckSquareIcon class="w-4 h-4 mr-1" />
                      {{ $t("views.edit") }}
                    </a>
                  </div>
                </td>
                <!-- Section-Actions:end -->
              </tr>
            </template>
          </tbody>
        </table>
      </div>
      <!-- Data-List:start -->
    </div>
    <!-- Main-container:end -->

    <!-- Pagination:start -->
    <PaginationControls
      v-model:page="pagination.page"
      v-model:per_page="pagination.per_page"
      :total_pages="pagination.total_pages"
      :total="pagination.total"
      :sizes="[3, 10, 25, 35, 50]"
      @request="getPurposes"
    >
      <template #summary>
        {{ $t("components.pagination.viewing", { total: purposes.length }) }}
      </template>
    </PaginationControls>
    <!-- Pagination:end -->
  </div>
</template>

<script>
import NutrihubService from "@/services/nutrihub.service";
import PaginationControls from "@/components/pagination-control/Main.vue";

export default {
  name: "SupplementPurposesList",
  components: {
    PaginationControls,
  },
  data() {
    return {
      // Data
      purposes: [],
      // Common
      viewRoot: "views.supplements_purposes",
      searchInput: "",
      debounceTimeout: null,
      // Pagination
      pagination: {
        page: 0,
        total_pages: 0,
        per_page: 10,
        total: 0,
      },
    };
  },
  mounted() {
    this.getInitialData();
  },
  watch: {
    "$i18n.locale": function (l) {
      this.getInitialData();
    },
  },
  methods: {
    /**
     * *Initial Data
     */
    async getInitialData() {
      await this.getPurposes();
    },
    async getPurposes() {
      const params = {
        search: this.searchInput || "",
        pagination: this.pagination,
      };
      const { data, pagination } = await NutrihubService.getPurposes(localStorage.lang, params);
      this.purposes = data;
      this.paginationSetData(pagination);
    },
    filterPurposes() {
      clearTimeout(this.debounceTimeout);

      this.debounceTimeout = setTimeout(() => {
        this.pagination.page = 0;
        this.getPurposes();
      }, 1000);
    },

    newPurpose() {
      this.$router.push({ path: "/supplement-purposes-new" });
    },

    editPurpose(id) {
      this.$router.push({ path: "/supplement-purposes-edit", query: { id } });
    },

    // Pagination:start
    paginationSetData({ page = 0, per_page: perPage = 0, total = 0, total_pages: totalPages = 0 } = {}) {
      this.pagination.page = page;
      this.pagination.per_page = perPage;
      this.pagination.total = total;
      this.pagination.total_pages = totalPages;
    },
    // Pagination:end
  },
};
</script>

<style lang="scss" scoped>
.purposes__grid {
  display: grid;
  // Default for smaller screens, adjust as needed
  grid-template-columns: repeat(auto-fit, minmax(100px, 1fr));
  align-items: center;
  width: 100%;
  gap: 10px; // Add some gap between items

  @media screen and (min-width: 768px) {
    // Adjust breakpoint for tablets/desktops
    grid-template-columns: 0.5fr 2fr 1.5fr 1.5fr 1.5fr; // Example for 5 columns on wider screens
  }

  @media screen and (min-width: 1400px) {
    grid-template-columns: 0.5fr 3fr 1.5fr 1.5fr 1.5fr; // Further adjustment for very wide screens
  }

  .title__table,
  .content__table {
    text-wrap: wrap;
    overflow: hidden; // Ensure text doesn't overflow
    text-overflow: ellipsis; // Add ellipsis for overflowing text
  }
}
</style>
